
import { useState, useEffect } from "react";
import { NoDataView } from "./chart/NoDataView";
import { GrowthLineChart } from "./chart/GrowthLineChart";
import { ChartContainer } from "@/components/ui/chart";
import { useGrowthChartData } from "./chart/data/GrowthChartData";
import { Card } from "@/components/ui/card";
import { ChartControls } from "./chart/ChartControls";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

export interface GrowthChartProps {
  title: string;
  description: string;
  chartData: any[];
  displayOriginalValue: number | null;
  useMetricUnit: boolean;
  handleUnitToggle: () => void;
  unitLabel: string;
  averageData: Array<{ month: number, value: number }>;
  getAverageValueForAge: (ageMonths: number) => number;
  valueKey: string;
  chartConfig: any;
  babyAgeInMonths: number;
}

export function GrowthChart({ 
  title,
  description,
  chartData, 
  displayOriginalValue, 
  useMetricUnit, 
  handleUnitToggle,
  unitLabel,
  averageData,
  getAverageValueForAge,
  valueKey,
  chartConfig,
  babyAgeInMonths
}: GrowthChartProps) {
  const [showAverageLine, setShowAverageLine] = useState(true); // Set to true by default
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { language } = useLanguage();
  
  // Handle transition effects when props change
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), 300);
    return () => clearTimeout(timer);
  }, [useMetricUnit, chartData.length]);
  
  // For better debugging
  useEffect(() => {
    console.log(`[GrowthChart] chartData length: ${chartData.length}`);
    console.log(`[GrowthChart] showAverageLine: ${showAverageLine}`);
    console.log(`[GrowthChart] valueKey: ${valueKey}`);
    console.log(`[GrowthChart] babyAgeInMonths: ${babyAgeInMonths}`);
  }, [showAverageLine, chartData, valueKey, babyAgeInMonths]);

  // Get prepared data for the chart
  const { allDataPoints, preparedAverageData, yAxisDomain } = useGrowthChartData({
    chartData,
    displayOriginalValue,
    showAverageLine,
    getAverageValueForAge,
    valueKey,
    babyAgeInMonths
  });

  return (
    <Card className="w-full h-full overflow-hidden rounded-lg border">
      <div className="p-4 border-b">
        <ChartControls
          title={title}
          description={description}
          showAverageLine={showAverageLine}
          onShowAverageChange={setShowAverageLine}
          useMetricUnit={useMetricUnit}
          unitLabel={unitLabel}
          handleUnitToggle={handleUnitToggle}
          valueKey={valueKey}
        />
      </div>
      <div className="w-full h-[500px] p-4">
        {chartData.length > 0 ? (
          <div className="w-full h-full">
            <ChartContainer config={chartConfig}>
              <GrowthLineChart
                data={allDataPoints}
                showAverageLine={showAverageLine}
                averageData={preparedAverageData}
                displayOriginalValue={displayOriginalValue}
                valueKey={valueKey}
                unitLabel={unitLabel}
                yAxisDomain={yAxisDomain}
                chartConfig={chartConfig}
              />
            </ChartContainer>
          </div>
        ) : (
          <NoDataView valueKey={valueKey} />
        )}
      </div>
    </Card>
  );
}

